import { useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { cancelOrder } from "@/lib/api";

const MOBILEPAY_QR_IMAGE =
  "https://customer-assets.emergentagent.com/job_af-toj-kiosk/artifacts/t48hbu6a_qr%20kode%20mobilepay.png";
const MOBILEPAY_LINK = "https://qr.mobilepay.dk/vp/Q_5s6o9zM";

export const ReceiptModal = ({ order, mobilepayBox, onClose, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  if (!order) return null;

  const comment = `${order.customer_name} #${order.order_number}`;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelOrder(order.id);
      toast.success("Bestillingen er fortrudt");
      onCancel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke fortryde bestillingen");
      setCancelling(false);
      setConfirmingCancel(false);
    }
  };

  return (
    <div
      data-testid="receipt-modal"
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-emerald-500 px-8 py-5 flex items-center gap-4 shrink-0">
          <div className="bg-white rounded-full p-2">
            <Check size={28} className="text-emerald-500" strokeWidth={3} />
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold text-white">Tak for din bestilling!</h2>
            <p className="text-emerald-50 font-semibold">Bestilling #{order.order_number}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Left: order summary */}
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900 mb-3">Bestilling</h3>
            <div className="text-sm text-slate-500 font-semibold mb-3">
              {order.customer_name} · {order.phone}
            </div>
            <ul className="space-y-2 mb-4">
              {order.items.map((item, idx) => (
                <li key={idx} className="flex justify-between bg-slate-50 rounded-lg p-3">
                  <div>
                    <div className="font-bold text-slate-900">{item.product_name}</div>
                    <div className="text-sm text-slate-500 font-semibold">
                      {item.size} · {item.color_label}
                    </div>
                  </div>
                  <div className="font-display font-bold text-slate-900">{item.price} kr</div>
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-center border-t-2 border-slate-200 pt-3">
              <span className="font-bold text-slate-500">I alt</span>
              <span className="font-display text-3xl font-bold text-slate-900">
                {order.total} kr
              </span>
            </div>

            {/* Cancel button under the order */}
            <button
              data-testid="btn-cancel-order"
              onClick={handleCancel}
              disabled={cancelling}
              className="mt-5 h-12 w-full rounded-xl bg-red-50 text-red-600 font-display text-base font-bold border-2 border-red-100 hover:bg-red-100 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <X size={18} strokeWidth={3} />
              {cancelling ? "Fortryder..." : "Fortryd bestilling"}
            </button>
          </div>

          {/* Right: MobilePay */}
          <div className="bg-[#F4F6FF] rounded-2xl p-6 flex flex-col items-center">
            <div className="text-sm font-bold text-[#5A78FF] uppercase tracking-wider mb-2">
              Betal med MobilePay
            </div>
            <div className="font-display text-4xl font-bold text-slate-900 mb-1">
              {order.total} kr
            </div>
            <div className="text-sm font-semibold text-slate-500 mb-4">
              <span className="text-slate-900">#{mobilepayBox}</span>
            </div>

            <div className="bg-white rounded-xl p-3 shadow-sm mb-4">
              <img
                src={MOBILEPAY_QR_IMAGE}
                alt="MobilePay QR-kode"
                className="w-52 h-52 object-contain"
              />
            </div>

            <a
              data-testid="btn-mobilepay-link"
              href={MOBILEPAY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-4 h-12 w-full rounded-xl bg-[#5A78FF] text-white font-display text-base font-bold flex items-center justify-center gap-2 active:scale-95 hover:bg-[#4A66E8] transition-all"
            >
              Åbn MobilePay på telefonen
            </a>

            <div className="text-center text-sm font-semibold text-slate-700 leading-relaxed">
              Når du betaler med MobilePay,
              kunne det være fedt hvis du skrev{" "}
              <span className="font-display font-bold text-slate-900 whitespace-nowrap">
                #{order.order_number}
              </span>{" "}
              som kommentar, så vi kan se du har betalt.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 py-5 border-t-2 border-slate-200 shrink-0">
          <button
            data-testid="btn-close-receipt"
            onClick={onClose}
            className="h-16 w-full bg-slate-900 text-white rounded-2xl font-display text-xl font-bold active:scale-95 hover:bg-slate-800 transition-all"
          >
            Færdig — Ny bestilling
          </button>
        </div>
      </div>
    </div>
  );
};
